import { useMemo } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

export const ORG_LOGO_BUCKET = "org-logos";

export type OrgLogoOrg = {
  id: string;
  name?: string | null;
  /** Storage path in the public `org-logos` bucket, or null when the org has no logo. */
  logo_url?: string | null;
};

/**
 * Initials shown while an org has no logo — the first letters of its first two
 * words ("What If" → "WI"), falling back to a single letter.
 */
export function getOrgInitials(name: string | null | undefined): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

/** Resolves a storage path in the `org-logos` bucket to its public URL. */
export function resolveOrgLogoUrl(logoUrl: string | null | undefined): string | null {
  if (!logoUrl) return null;
  if (logoUrl.startsWith("http://") || logoUrl.startsWith("https://")) return logoUrl;
  const { data } = supabase.storage.from(ORG_LOGO_BUCKET).getPublicUrl(logoUrl);
  return data.publicUrl;
}

export interface OrgLogoProps {
  org: OrgLogoOrg | null | undefined;
  size?: number;
  className?: string;
}

/**
 * An organization's logo (its profile picture), rendered as a rounded square —
 * a company mark, deliberately distinct from the circular UserAvatar.
 *
 * No cache-busting needed: every upload writes a new path (see `useOrgLogo`), so a
 * replaced logo is a different URL here and in every other consumer of `logo_url`.
 */
export function OrgLogo({ org, size = 24, className }: OrgLogoProps) {
  const src = useMemo(() => resolveOrgLogoUrl(org?.logo_url), [org?.logo_url]);

  if (!org) return null;

  const name = org.name ?? "";

  return (
    <Avatar
      className={cn("rounded-lg font-semibold", className)}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      title={name}
    >
      {src ? <AvatarImage src={src} alt={name} className="object-cover" /> : null}
      <AvatarFallback
        className="rounded-lg bg-muted text-muted-foreground"
        style={{ fontSize: size * 0.4 }}
      >
        {getOrgInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
}
